import type { BigQueryUsageMonitoring } from "@/features/bigquery-usage/api/bigquery-usage.api";

/**
 * The width of the band the two sides are allowed to differ by before the panel
 * calls it a discrepancy. Two percent, the same figure the Vertex reconciliation
 * panel uses, and for the same reason: Cloud Monitoring lands its points on its
 * own ingestion delay, so the tail of every window is still filling in while it
 * is being read. Below this, a gap is that lag plus rounding — a number nobody
 * can act on — and reporting it as a disagreement would train an operator to
 * ignore the panel that is supposed to catch a real one.
 */
export const RECONCILED_TOLERANCE = 0.02;

/**
 * Unavailable is Cloud Monitoring not being readable at all, which is this
 * panel's own degrade state under R20 rather than a state of the telemetry.
 */
export type BigQueryReconciliationStatus = "unavailable" | "agreement" | "discrepancy";

/** One measure, from both sides. */
export interface BigQueryMetricReconciliation {
  /** False when Cloud Monitoring did not return this metric at all. */
  isAvailable: boolean;
  recorded: number;
  /**
   * What Google reported, rounded. Monitoring sums deltas as floats, and a
   * fractional query count is an artefact of that sum rather than a measurement.
   */
  reported: number;
  /** Unsigned; `isRecordedShort` carries the direction. */
  gap: number;
  /** The ordinary direction — Nexus below Google — which R21's causes explain. */
  isRecordedShort: boolean;
  /** Null when there is no reported figure to take a share of. */
  gapShare: number | null;
  /** Only meaningful while `isAvailable`. */
  isWithinTolerance: boolean;
}

export interface BigQueryReconciliationSummary {
  status: BigQueryReconciliationStatus;
  queries: BigQueryMetricReconciliation;
  bytesBilled: BigQueryMetricReconciliation;
}

/** What Nexus recorded for the same window, to compare against. */
export interface BigQueryRecordedTotals {
  queries: number;
  bytesBilled: number;
}

/**
 * Whether what Nexus recorded accounts for what Google billed the project for.
 *
 * The two metrics are independent reads on Monitoring's side, so they are
 * compared independently: one of them answering is worth showing, and the one
 * that stayed silent must never be compared against the structural zero the
 * route sends in its place.
 */
export function summarizeBigQueryReconciliation(
  monitoring: BigQueryUsageMonitoring | undefined,
  recorded: BigQueryRecordedTotals,
): BigQueryReconciliationSummary {
  const queries = compareMetric(
    recorded.queries,
    monitoring?.totalQueries ?? 0,
    monitoring?.available === true && monitoring.queriesAvailable,
  );
  const bytesBilled = compareMetric(
    recorded.bytesBilled,
    monitoring?.totalBytesBilled ?? 0,
    monitoring?.available === true && monitoring.bytesBilledAvailable,
  );

  const compared = [queries, bytesBilled].filter((metric) => metric.isAvailable);
  const status: BigQueryReconciliationStatus =
    compared.length === 0
      ? "unavailable"
      : compared.every((metric) => metric.isWithinTolerance)
        ? "agreement"
        : "discrepancy";

  return { status, queries, bytesBilled };
}

function compareMetric(
  recorded: number,
  reportedRaw: number,
  isAvailable: boolean,
): BigQueryMetricReconciliation {
  const reported = Math.round(reportedRaw);
  const gap = Math.abs(reported - recorded);

  return {
    isAvailable,
    recorded,
    reported,
    gap,
    isRecordedShort: recorded < reported,
    gapShare: reported > 0 ? gap / reported : null,
    // A window Google reported nothing for has no band to be inside: the only
    // reading that agrees with zero is zero, and dividing by it would not say so.
    isWithinTolerance: reported === 0 ? recorded === 0 : gap / reported <= RECONCILED_TOLERANCE,
  };
}
